import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { trpc } from '@/api/trpc';
import { showToast } from '@/components/Layout';
import { Badge, Button, Card, FormCheckbox, TradingStrategySelect } from '@/components/UI';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useOptionBuyingStrategies } from '@/hooks/useOptionBuyingStrategies';
import { useTradingStrategies } from '@/hooks/useTradingStrategies';
import {
    type AeroCandidateReviewRow,
    formatAeroNumber,
    formatAeroPercent,
    formatAeroWinRatePercent
} from '@/features/tradeLogicAuthoring/components/aeroCandidateResultDetails';
import { isAeroCandidateSelectionSafe } from '@/features/tradeLogicAuthoring/components/aeroCandidatePromotionEligibility';
import ExitConfigChips from '@/features/tradeLogicAuthoring/components/ExitConfigChips';
import { usePromoteAeroCandidatesToGlobalCandidatesMutation } from '@/features/tradeLogicAuthoring/api';
import { type AeroObsPalette, getAeroObsTone } from '../aeroObsPalette';
import { formatAeroCompactNumber } from '../aeroPageUtils';
import { buildAeroUseResultPublicationState, canAttachAeroUseResult } from './aeroUseResultPublicationState';

/** The publish endpoint accepts at most 100 variant hashes per call. */
const MAX_SELECTION = 100;

interface AeroUseResultPanelProps {
    backendReady: boolean;
    jobId: number;
    jobStrategyId: number;
    palette: AeroObsPalette;
    selectedCandidates: AeroCandidateReviewRow[];
    onClearSelection: () => void;
}

/**
 * Applies a chosen AERO result to a strategy.
 *
 * Publication to a global exit candidate is plumbing, not a decision, so it happens implicitly for
 * any selected row that is not published yet. The two decisions a user actually makes — which exit
 * configuration a strategy may use, and which option-buying strategy it should buy — are the
 * explicit actions here.
 */
const AeroUseResultPanel = ({
    backendReady,
    jobId,
    jobStrategyId,
    palette,
    selectedCandidates,
    onClearSelection
}: AeroUseResultPanelProps) => {
    const queryClient = useQueryClient();
    const [targetStrategyId, setTargetStrategyId] = useState<number | null>(jobStrategyId);
    const [makeDefault, setMakeDefault] = useState(false);

    const { strategies } = useTradingStrategies();
    const targetStrategy = strategies.find((strategy) => strategy.id === targetStrategyId) ?? null;
    const currentOptionStrategyIds = useMemo(() => targetStrategy?.autoBacktestOptionBuyingStrategiesOverride ?? [], [targetStrategy]);

    // The job palette only covers axes this job tested; a strategy can list option strategies it did not.
    const { strategiesById: optionStrategiesById } = useOptionBuyingStrategies();
    const formatOptionStrategyName = (optionStrategyId: number) =>
        optionStrategiesById.get(optionStrategyId)?.name?.trim() || `OBS #${optionStrategyId}`;

    const attachmentsQuery = useAppQuery({
        ...trpc.tradeLogicExitCandidates.getStrategyCandidateAttachments.queryOptions({
            strategyId: targetStrategyId ?? 0
        }),
        enabled: targetStrategyId !== null,
        staleTime: 30000
    });

    const promoteMutation = usePromoteAeroCandidatesToGlobalCandidatesMutation();
    const attachMutation = useMutation(trpc.tradeLogicExitCandidates.attachGlobalCandidatesToStrategy.mutationOptions());
    const updateStrategyMutation = useMutation(trpc.strategy.updateStrategy.mutationOptions());

    const attachedCandidateIds = useMemo(
        () => new Set((attachmentsQuery.data?.candidates ?? []).map((candidate) => candidate.candidateId)),
        [attachmentsQuery.data]
    );

    const publicationState = useMemo(() => buildAeroUseResultPublicationState(selectedCandidates), [selectedCandidates]);
    /**
     * A global exit candidate covers a whole exit configuration, so rows from different option-buying
     * axes collapse onto the same id. Compare distinct ids, not row counts.
     */
    const selectedGlobalCandidateIds = useMemo(() => new Set(publicationState.globalCandidateIds), [publicationState.globalCandidateIds]);

    const selectedOptionStrategyIds = useMemo(
        () => Array.from(new Set(selectedCandidates.map((candidate) => candidate.optionBuyingStrategyId))),
        [selectedCandidates]
    );
    const singleOptionStrategyId =
        selectedOptionStrategyIds.length === 1 && selectedOptionStrategyIds[0] !== null ? selectedOptionStrategyIds[0] : null;
    const singleOptionStrategyLabel = singleOptionStrategyId === null ? null : formatOptionStrategyName(singleOptionStrategyId);

    const isFullyAttached =
        publicationState.isEverySelectionPublished &&
        Array.from(selectedGlobalCandidateIds).every((candidateId) => attachedCandidateIds.has(candidateId));

    const isOverSelectionLimit = selectedCandidates.length > MAX_SELECTION;
    const hasIneligibleSelection = selectedCandidates.some((candidate) => !isAeroCandidateSelectionSafe(candidate));
    const isBusy = promoteMutation.isPending || attachMutation.isPending;
    const canAttach =
        !hasIneligibleSelection &&
        canAttachAeroUseResult({
            backendReady,
            isBusy,
            isOverSelectionLimit,
            needsPromotion: publicationState.needsPromotion,
            selectedCount: selectedCandidates.length,
            targetStrategyId
        });

    // Result-row and candidate-coverage invalidation already happens inside the publish mutation.
    const refreshAfterAttach = async () => {
        await Promise.all([
            queryClient.invalidateQueries(trpc.tradeLogicExitCandidates.getStrategyCandidateAttachments.pathFilter()),
            queryClient.invalidateQueries(trpc.backtest.getStrategyCoverage.pathFilter())
        ]);
    };

    /**
     * Resolves every selected row to a global exit candidate id, publishing the ones that do not
     * have one yet. Publishing is idempotent: an already-published row returns its existing id.
     */
    const resolveGlobalCandidateIds = async (): Promise<number[]> => {
        if (!publicationState.needsPromotion) {
            return publicationState.globalCandidateIds;
        }

        const promotion = await promoteMutation.mutateAsync({
            jobId,
            variantHashes: publicationState.unpublishedVariantHashes
        });

        if (!promotion.success) {
            throw new Error(promotion.errorMessages.join(' ') || 'AERO results could not be published as global exit candidates');
        }

        return Array.from(
            new Set([...publicationState.globalCandidateIds, ...promotion.promotedCandidates.map((candidate) => candidate.candidateId)])
        );
    };

    const attachToStrategy = async () => {
        if (targetStrategyId === null || selectedCandidates.length === 0 || hasIneligibleSelection) return;

        try {
            const candidateIds = await resolveGlobalCandidateIds();

            if (candidateIds.length === 0) {
                showToast('The selected AERO results have no publishable exit candidate', 'error');
                return;
            }

            await attachMutation.mutateAsync({
                strategyId: targetStrategyId,
                candidateIds,
                ...(makeDefault && candidateIds[0] !== undefined ? { defaultCandidateId: candidateIds[0] } : {})
            });

            await refreshAfterAttach();
            showToast(
                `Attached ${candidateIds.length} exit configuration${candidateIds.length === 1 ? '' : 's'} to strategy ${targetStrategyId}`,
                'success'
            );
        } catch (error) {
            showToast(error instanceof Error ? error.message : 'Failed to attach the exit configuration', 'error');
        }
    };

    const applyOptionStrategy = async (nextOptionStrategyIds: number[]) => {
        if (targetStrategyId === null || hasIneligibleSelection) return;

        try {
            await updateStrategyMutation.mutateAsync({
                id: targetStrategyId,
                autoBacktestOptionBuyingStrategiesOverride: nextOptionStrategyIds
            });

            await queryClient.invalidateQueries(trpc.strategy.fetchStrategies.pathFilter());
            showToast(`Strategy ${targetStrategyId} option strategies updated`, 'success');
        } catch (error) {
            showToast(error instanceof Error ? error.message : 'Failed to update the strategy option strategies', 'error');
        }
    };

    return (
        <Card
            className="border-cyan-900/50"
            headerActions={
                selectedCandidates.length > 0 ? (
                    <Button size="sm" variant="outline-dark" onClick={onClearSelection}>
                        Clear
                    </Button>
                ) : undefined
            }
            subtitle="Apply a result from this job to a strategy."
            title="Use This Result"
        >
            {selectedCandidates.length === 0 ? (
                <div className="rounded-md border border-zinc-800 bg-black/20 p-3 text-sm text-zinc-400">
                    Tick a row in the results table, or choose a winner from the option-strategy leaderboard.
                </div>
            ) : (
                <div className="space-y-4">
                    <AeroSelectionSummary palette={palette} selectedCandidates={selectedCandidates} />

                    {hasIneligibleSelection && (
                        <div className="rounded-md border border-red-900/60 bg-red-950/40 p-3 text-sm text-red-100" role="alert">
                            This selection contains a result that failed AERO&apos;s data-coverage or drawdown gate. Clear it and choose an
                            eligible result before applying anything to a strategy.
                        </div>
                    )}

                    {publicationState.globalCandidateIds.length > 0 && (
                        <div
                            aria-live="polite"
                            className="rounded-md border border-green-900/60 bg-green-950/25 p-3 text-sm text-green-200"
                        >
                            Already global ·{' '}
                            {publicationState.globalCandidateIds.map((candidateId) => `Exit Candidate #${candidateId}`).join(', ')}
                        </div>
                    )}

                    {isOverSelectionLimit && (
                        <div className="rounded-md border border-amber-900/60 bg-amber-950/30 p-3 text-sm text-amber-100">
                            Select {MAX_SELECTION} results or fewer to apply them.
                        </div>
                    )}

                    <div className="space-y-3 rounded-md border border-zinc-800 bg-black/20 p-3">
                        <div className="flex items-center gap-2">
                            <StepNumber value={1} />
                            <span className="text-sm font-medium text-zinc-100">Exit configuration</span>
                        </div>

                        <TradingStrategySelect
                            noMargin
                            helpText={targetStrategyId === jobStrategyId ? 'This job’s strategy.' : 'Applying to a different strategy.'}
                            label="Strategy"
                            value={targetStrategyId}
                            onChange={setTargetStrategyId}
                        />

                        <FormCheckbox
                            checked={makeDefault}
                            id="aero-use-result-default"
                            label="Make it this strategy’s default exit"
                            labelClassName="text-zinc-300"
                            onChange={(event) => setMakeDefault(event.target.checked)}
                        />

                        <Button fullWidth disabled={!canAttach} isLoading={isBusy} size="sm" onClick={attachToStrategy}>
                            {isFullyAttached ? 'Re-apply to strategy' : 'Attach exit to strategy'}
                        </Button>

                        {isFullyAttached && (
                            <div className="text-xs text-green-300">
                                Already attached to strategy {targetStrategyId}
                                {makeDefault ? ' — re-applying will also set it as the default.' : '.'}
                            </div>
                        )}
                        {publicationState.needsPromotion && !backendReady && (
                            <div className="text-xs text-amber-200">Waiting for the AERO publication endpoint.</div>
                        )}
                    </div>

                    <div className="space-y-3 rounded-md border border-zinc-800 bg-black/20 p-3">
                        <div className="flex items-center gap-2">
                            <StepNumber value={2} />
                            <span className="text-sm font-medium text-zinc-100">Option buying strategy</span>
                        </div>

                        {singleOptionStrategyId === null ? (
                            <div className="text-xs text-zinc-400">
                                {selectedOptionStrategyIds.length > 1
                                    ? `The selection spans ${selectedOptionStrategyIds.length} option strategies. Select rows from a single axis to set one.`
                                    : 'This result is not tied to an option-buying strategy.'}
                            </div>
                        ) : (
                            <>
                                <div>
                                    <div className="mb-1 text-xs uppercase tracking-wide text-zinc-500">
                                        Strategy {targetStrategyId} currently tests
                                    </div>
                                    {currentOptionStrategyIds.length === 0 ? (
                                        <div className="text-xs text-zinc-400">All available option strategies.</div>
                                    ) : (
                                        <div className="flex flex-wrap gap-1">
                                            {currentOptionStrategyIds.map((optionStrategyId) => (
                                                <Badge
                                                    key={optionStrategyId}
                                                    variant={optionStrategyId === singleOptionStrategyId ? 'success' : 'dark'}
                                                >
                                                    {formatOptionStrategyName(optionStrategyId)}
                                                </Badge>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="flex flex-wrap gap-2">
                                    <Button
                                        disabled={hasIneligibleSelection || targetStrategyId === null || updateStrategyMutation.isPending}
                                        isLoading={updateStrategyMutation.isPending}
                                        size="sm"
                                        onClick={() => applyOptionStrategy([singleOptionStrategyId])}
                                    >
                                        Use only this one
                                    </Button>
                                    {!currentOptionStrategyIds.includes(singleOptionStrategyId) && (
                                        <Button
                                            disabled={
                                                hasIneligibleSelection || targetStrategyId === null || updateStrategyMutation.isPending
                                            }
                                            size="sm"
                                            variant="outline-dark"
                                            onClick={() => applyOptionStrategy([...currentOptionStrategyIds, singleOptionStrategyId])}
                                        >
                                            Add to list
                                        </Button>
                                    )}
                                </div>

                                <div className="text-xs text-zinc-500">
                                    Narrows which option strategies this strategy’s automated backtests buy. Winner:{' '}
                                    {singleOptionStrategyLabel}.
                                </div>
                            </>
                        )}
                    </div>

                    <div className="rounded-md border border-zinc-800 bg-black/20 p-3 text-xs text-zinc-400">
                        Prefer AERO to pick a different exit per trade date instead of pinning one? Use{' '}
                        <span className="text-zinc-200">PIT Exit Selection</span> below — it needs the exit attached above first.
                    </div>
                </div>
            )}
        </Card>
    );
};

const StepNumber = ({ value }: { value: number }) => (
    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-cyan-900/60 text-[11px] font-semibold text-cyan-100">
        {value}
    </span>
);

const AeroSelectionSummary = ({
    palette,
    selectedCandidates
}: {
    palette: AeroObsPalette;
    selectedCandidates: AeroCandidateReviewRow[];
}) => {
    const [primary] = selectedCandidates;
    const extraCount = selectedCandidates.length - 1;
    const tone = getAeroObsTone(palette, primary.optionBuyingStrategyId);

    return (
        <div className="rounded-md border border-zinc-800 bg-zinc-950/60 p-3">
            <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className={`inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-xs font-medium ${tone.chipClassName}`}>
                    <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${tone.dotClassName}`} />
                    <span className="truncate">{tone.shortLabel}</span>
                </span>
                {extraCount > 0 && <Badge variant="info">+{extraCount} more selected</Badge>}
            </div>

            <ExitConfigChips candidate={primary} />

            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                <SummaryMetric label="Selected score" value={formatAeroNumber(primary.selectedLiveScore)} />
                <SummaryMetric label="Raw avg profit" value={formatAeroPercent(primary.avgProfit)} />
                <SummaryMetric label="Winsorized avg" value={formatAeroPercent(primary.winsorizedAvgProfit)} />
                <SummaryMetric label="Win rate" value={formatAeroWinRatePercent(primary.winRate)} />
                <SummaryMetric label="Trades" value={formatAeroCompactNumber(primary.totalTrades)} />
            </div>
        </div>
    );
};

const SummaryMetric = ({ label, value }: { label: string; value: string }) => (
    <div>
        <div className="text-[10px] uppercase tracking-wide text-zinc-500">{label}</div>
        <div className="text-sm font-medium text-zinc-100">{value}</div>
    </div>
);

export default AeroUseResultPanel;
